<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import IconBox from '@uiKit/components/IconBox.vue'
import depositIcon from '@assets/images/deposite.svg'
import investmentIcon from '@assets/images/investment.svg'
import rentalIncomeIcon from '@assets/images/rental_income.svg'
import withdrawalIcon from '@assets/images/withdrawal.svg'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import type { Transaction, TransactionStatus, TransactionType } from '../types/transactionTypes'

interface Props {
  transaction: Transaction
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'view-details', tx: Transaction): void
}>()

// Copy-reference state mirrors BankAccountDetailsDrawer's pattern — show
// "check" briefly after a successful clipboard write so the user gets
// visible feedback without a toast.
const copied = ref(false)
const COPY_FEEDBACK_MS = 1600
let copyTimer: ReturnType<typeof setTimeout> | null = null

async function copyReferenceId(event: Event) {
  event.stopPropagation()
  const value = props.transaction.referenceId
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
    copied.value = true
    if (copyTimer) clearTimeout(copyTimer)
    copyTimer = setTimeout(() => {
      copied.value = false
    }, COPY_FEEDBACK_MS)
  } catch {
    // clipboard unavailable — silent fallback
  }
}

// Middle-truncate a reference id so the prefix (SL-DEP, SL-WDR) and the
// trailing characters of the uuid both stay visible. Pure CSS ellipsis loses
// the tail which is what most users actually compare against support.
function middleTruncate(value: string, headLen = 10, tailLen = 5): string {
  if (value.length <= headLen + tailLen + 1) return value
  return `${value.slice(0, headLen)}…${value.slice(-tailLen)}`
}

interface IconConfig {
  icon: PiIconName
  imageSrc?: string
  bg: string
  color: string
}

// TODO(design): token-received "completed" uses a bespoke lime green
// (#f5fee7 / #58a40c) that has no semantic match in slicealPreset.ts. Either
// promote it to `--p-sliceal-token-*` or remap to success.
const TOKEN_COMPLETED_BG = 'var(--p-sliceal-token-bg-light)'
const TOKEN_COMPLETED_COLOR = 'var(--p-sliceal-token-text)'

const TYPE_ICON: Record<Exclude<TransactionType, 'token_received'>, IconConfig> = {
  deposit: {
    icon: 'arrow-circle-down',
    imageSrc: depositIcon,
    bg: 'var(--p-success-50)',
    color: 'var(--p-success-700)',
  },
  investment: {
    icon: 'wallet',
    imageSrc: investmentIcon,
    bg: 'var(--p-primary-50)',
    color: 'var(--p-primary-600)',
  },
  rental_income: {
    icon: 'home',
    imageSrc: rentalIncomeIcon,
    bg: 'var(--p-info-50)',
    color: 'var(--p-info-500)',
  },
  withdrawal: {
    icon: 'arrow-circle-up',
    imageSrc: withdrawalIcon,
    bg: 'var(--p-danger-50)',
    color: 'var(--p-danger-500)',
  },
  refund: { icon: 'refresh', bg: 'var(--p-success-50)', color: 'var(--p-success-700)' },
  rental: { icon: 'home', bg: 'var(--p-info-50)', color: 'var(--p-info-500)' },
  secondary_purchase: {
    icon: 'shopping-bag',
    bg: 'var(--p-primary-50)',
    color: 'var(--p-primary-600)',
  },
  secondary_sale_proceeds: {
    icon: 'tag',
    bg: 'var(--p-success-50)',
    color: 'var(--p-success-700)',
  },
}

const TOKEN_ICON_BY_STATUS: Partial<Record<TransactionStatus, IconConfig>> = {
  completed: { icon: 'tag', bg: TOKEN_COMPLETED_BG, color: TOKEN_COMPLETED_COLOR },
  processing: { icon: 'tag', bg: 'var(--p-info-50)', color: 'var(--p-info-500)' },
  pending: { icon: 'tag', bg: 'var(--p-warning-50)', color: 'var(--p-warning-700)' },
  failed: { icon: 'tag', bg: 'var(--p-danger-50)', color: 'var(--p-danger-500)' },
  reversed: { icon: 'tag', bg: 'var(--p-surface-100)', color: 'var(--p-text-muted-color)' },
}

const FALLBACK_ICON: IconConfig = {
  icon: 'tag',
  bg: 'var(--p-surface-100)',
  color: 'var(--p-text-muted-color)',
}

function getIconConfig(tx: Transaction): IconConfig {
  if (tx.type === 'token_received') return TOKEN_ICON_BY_STATUS[tx.status] ?? FALLBACK_ICON
  return TYPE_ICON[tx.type] ?? FALLBACK_ICON
}

interface StatusConfig {
  label: string
  bg: string
  color: string
  icon: PiIconName
}

const STATUS_CONFIG: Record<TransactionStatus, StatusConfig> = {
  completed: {
    label: 'Completed',
    bg: 'var(--p-success-100)',
    color: 'var(--p-success-700)',
    icon: 'check-circle',
  },
  pending: {
    label: 'Pending',
    bg: 'var(--p-warning-100)',
    color: 'var(--p-warning-700)',
    icon: 'clock',
  },
  processing: {
    label: 'Processing',
    bg: 'var(--p-info-100)',
    color: 'var(--p-info-500)',
    icon: 'refresh',
  },
  failed: {
    label: 'Failed',
    bg: 'var(--p-danger-50)',
    color: 'var(--p-danger-700)',
    icon: 'exclamation-circle',
  },
  reversed: {
    label: 'Reversed',
    bg: 'var(--p-surface-100)',
    color: 'var(--p-text-muted-color)',
    icon: 'refresh',
  },
}

function getAmountColor(tx: Transaction): string {
  if (tx.type === 'token_received') return TOKEN_COMPLETED_COLOR
  return tx.incoming ? 'var(--p-success-700)' : 'var(--p-text-color)'
}

const onViewDetails = () => emit('view-details', props.transaction)
</script>

<template>
  <Box class="tx-row">
    <Box align="center" gap="3" class="tx-row__detail">
      <IconBox
        :icon="getIconConfig(transaction).icon"
        :image-src="getIconConfig(transaction).imageSrc"
        :image-alt="`${transaction.category} icon`"
        :bg="getIconConfig(transaction).bg"
        :icon-color="getIconConfig(transaction).color"
        size="xl"
      />
      <Box direction="col" gap="1" class="tx-row__info">
        <Text type="body" weight="medium" truncate class="tx-row__title">
          {{ transaction.title }}
        </Text>
        <Text type="caption" weight="medium" color="muted" truncate class="tx-row__sub">
          {{ transaction.category }}
        </Text>
      </Box>
    </Box>

    <Box direction="col" align="start" justify="center" gap="0" class="tx-row__date">
      <Text type="body-sm" weight="medium" is="span">{{ transaction.date }}</Text>
      <Text v-if="transaction.time" type="caption" color="muted" is="span">
        {{ transaction.time }}
      </Text>
    </Box>

    <Box align="center" gap="1" class="tx-row__txnid">
      <Text
        v-if="transaction.referenceId"
        type="body-sm"
        weight="medium"
        is="span"
        class="tx-row__txnid-value"
        :title="transaction.referenceId"
      >
        {{ middleTruncate(transaction.referenceId) }}
      </Text>
      <Text v-else type="body-sm" color="muted" is="span">-</Text>
      <Box
        v-if="transaction.referenceId"
        is="button"
        type="button"
        align="center"
        justify="center"
        class="tx-row__copy"
        :aria-label="copied ? 'Reference ID copied' : 'Copy reference ID'"
        :title="copied ? 'Copied!' : 'Copy reference ID'"
        @click="copyReferenceId"
      >
        <PiIcon :name="copied ? 'check' : 'copy'" class="tx-row__copy-icon" />
      </Box>
    </Box>

    <Text
      type="body"
      weight="semibold"
      align="center"
      is="div"
      class="tx-row__amount"
      :color="getAmountColor(transaction)"
    >
      <CurrencyText :text="transaction.amount" />
    </Text>

    <Box align="center" justify="center" class="tx-row__status">
      <Box
        align="center"
        gap="1"
        class="tx-row__chip"
        :style="{ background: STATUS_CONFIG[transaction.status].bg }"
      >
        <PiIcon
          :name="STATUS_CONFIG[transaction.status].icon"
          class="tx-row__chip-icon"
          :style="{ color: STATUS_CONFIG[transaction.status].color }"
        />
        <Text
          type="micro"
          is="span"
          class="tx-row__chip-label"
          :color="STATUS_CONFIG[transaction.status].color"
        >
          {{ STATUS_CONFIG[transaction.status].label }}
        </Text>
      </Box>
    </Box>

    <Box align="center" justify="center" class="tx-row__view">
      <AppButton
        label="View"
        icon="pi pi-chevron-right"
        icon-pos="right"
        variant="link"
        color="primary"
        @click="onViewDetails"
      />
    </Box>
  </Box>
</template>

<style scoped>
.tx-row {
  display: grid;
  grid-template-columns:
    minmax(220px, 2.4fr) minmax(120px, 1.4fr) minmax(150px, 1.6fr) minmax(110px, 1.2fr)
    minmax(110px, 1.2fr) minmax(100px, 1fr);
  column-gap: 0.75rem;
  align-items: center;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 10px;
  padding: 14px 1.25rem;
  min-height: 72px;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.04);
  box-sizing: border-box;
  transition:
    box-shadow 0.15s,
    border-color 0.15s;
}

.tx-row:hover {
  border-color: var(--p-primary-200);
  box-shadow: 0 2px 8px rgba(10, 13, 18, 0.06);
}

.tx-row__detail {
  min-width: 0;
  padding-right: 0.75rem;
  overflow: hidden;
}

.tx-row__info {
  min-width: 0;
}

.tx-row__title {
  line-height: 20px;
}

.tx-row__sub {
  line-height: 18px;
}

.tx-row__date {
  min-width: 0;
  line-height: 1.4;
}

.tx-row__txnid {
  min-width: 0;
  overflow: hidden;
}

.tx-row__txnid-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
}

.tx-row__copy {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  color: var(--p-text-muted-color);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.12s,
    color 0.12s,
    border-color 0.12s;
}

.tx-row__copy:hover {
  background: var(--p-primary-50);
  color: var(--p-primary-600);
  border-color: var(--p-primary-100);
}

.tx-row__copy:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 1px;
}

.tx-row__copy-icon {
  font-size: 12px;
}

.tx-row__amount {
  min-width: 0;
  line-height: 24px;
  font-variant-numeric: tabular-nums;
}

.tx-row__status {
  min-width: 0;
}

.tx-row__chip {
  display: inline-flex;
  padding: 3px 10px;
  min-height: 24px;
  border-radius: 999px;
  max-width: 100%;
}

.tx-row__chip-label {
  line-height: 1.3;
  word-break: break-word;
}

.tx-row__chip-icon {
  font-size: 11px;
}

.tx-row__view {
  min-width: 0;
}

@media (max-width: 992px) {
  .tx-row {
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto auto auto;
    grid-template-areas:
      'detail  amount'
      'date    date'
      'txnid   txnid'
      'status  view';
    gap: 5px 8px;
    align-items: start;
    padding: 14px 1rem;
    min-height: unset;
  }

  .tx-row__detail,
  .tx-row__date,
  .tx-row__txnid,
  .tx-row__amount,
  .tx-row__status,
  .tx-row__view {
    border-right: none;
    padding: 0;
  }

  .tx-row__detail {
    grid-area: detail;
    align-self: start;
  }

  .tx-row__amount {
    grid-area: amount;
    justify-content: flex-end;
    text-align: right;
    font-size: 15px;
    font-weight: 700;
    align-self: start;
  }

  .tx-row__date {
    grid-area: date;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    justify-content: flex-start;
    font-size: 12px;
  }

  .tx-row__txnid {
    grid-area: txnid;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-size: 11px;
    color: var(--p-surface-400);
  }

  .tx-row__status {
    grid-area: status;
    display: flex;
    justify-content: flex-start;
    align-self: center;
  }

  .tx-row__view {
    grid-area: view;
    display: flex;
    justify-content: flex-end;
    align-self: center;
  }
}
</style>
